import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FairPriceMethodEnum } from '@app/common';
import { useRootStore } from '../../../../providers/root-store-provider';
import { BaseLineChart } from '../../chart/base/line/base-line-chart';

export const FairPriceChart: React.FC = observer(() => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [isFullScreen, setIsFullScreen] = useState(false);
    const {
        symbolsStore: { symbolFairPriceStore, symbolDetailStore },
    } = useRootStore();

    const {
        projectionChartData: chartData,
        currentPrice,
        activeFairPrice,
        inputGrowthRate,
        inputGrowthRateFCF,
        inputDcfGrowthRate,
        activeTab,
    } = symbolFairPriceStore;

    const currency = symbolDetailStore?.general?.symbol?.currency || 'USD';

    let growthRate: number | null;
    if (activeTab === FairPriceMethodEnum.DCF) {
        growthRate = inputDcfGrowthRate;
    } else if (activeTab === FairPriceMethodEnum.FCF) {
        growthRate = inputGrowthRateFCF ?? inputGrowthRate;
    } else {
        growthRate = inputGrowthRate;
    }

    if (!chartData[0]?.data.some((d) => d.y > 0)) return null;

    const isPositive = activeFairPrice >= currentPrice;
    const intrinsicColor = (
        isPositive ? theme.palette.color.positive.main : theme.palette.color.negative.main
    ) as string;
    const currentColor = theme.palette.color.secondary.main as string;

    const fairPriceLabel =
        activeTab === FairPriceMethodEnum.DCF
            ? t('layout.dialogs.fairPrice.chart.intrinsicValueDcf')
            : t('layout.dialogs.fairPrice.chart.intrinsicValue');
    const currentPriceLabel = t('layout.dialogs.fairPrice.chart.currentPrice');

    const colorOverrides: Record<string, string> = {
        intrinsicValue: intrinsicColor,
        currentPrice: currentColor,
    };

    const seriesLabels: Record<string, string> = {
        intrinsicValue: fairPriceLabel,
        currentPrice: currentPriceLabel,
    };

    const filter = new Map<string, boolean>([
        ['intrinsicValue', true],
        ['currentPrice', true],
    ]);

    return (
        <>
            <div className="-ml-xsmall">
                <BaseLineChart
                    chartData={chartData}
                    unit={currency}
                    dateFormat="YYYY [Q]Q"
                    labelDateFormat="YYYY [Q]Q"
                    axisBottomTickValues="every 1 year"
                    axisLeftMinMargin={72}
                    filter={filter}
                    dataOverride
                    colorOverrides={colorOverrides}
                    seriesLabels={seriesLabels}
                    isFullScreen={isFullScreen}
                    toggleFullScreen={() => setIsFullScreen((v) => !v)}
                    showFullScreenTrigger={false}
                />
            </div>
            <div className="mt-[0.4rem] text-center text-vsmall text-text-muted">
                {t('layout.dialogs.fairPrice.chart.growthNote', { rate: growthRate })}
            </div>
        </>
    );
});
